import { Box, Typography, Divider, Button, Card, CardMedia, CardContent, Modal, IconButton } from "@mui/material";
import { t } from "i18next";
import { useState, useEffect } from "react";
import { useSetRecoilState } from "recoil";
import { CloseIcon, BackupTableIcon } from "../../icons";
import { isCreatingReportState, selectedReportListState } from "../../recoil";
import { ContainedButtonStyles } from "../../styles/GlobalStyles";
import { EmptyTemplate } from "../../utils/utilsComponent";
import { PeriodSelector, GridPreview } from "./TemplateModal";
import report1 from "../../assets/report1.png";
import report2 from "../../assets/report2.png";
import report3 from "../../assets/report3.png";
import report4 from "../../assets/report4.png";
import report5 from "../../assets/report5.png";
import templateResponse from "../../datas/templates.json";
import { saveReportConfig } from "../../services/reportService";
import { ReportItem } from "../../@types";

interface periodItems {
  year: number;
  quarter?: number;
}

interface CreateNewReportProps {
  selectedPeriod: periodItems | null;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<periodItems | null>>;
  dateRange: {
    start_date: string;
    end_date: string;
  };
  setDateRange: (dateRange: { start_date: string; end_date: string }) => void;
  type: any;
}

export const NewReport = ({
  selectedPeriod,
  setSelectedPeriod,
  dateRange,
  setDateRange,
  type,
}: CreateNewReportProps) => {
  const [templateList, setTemplateList] = useState<templateItem[]>([]);
  const setIsCreatingReport = useSetRecoilState(isCreatingReportState);
  const setSelectedReportList = useSetRecoilState(selectedReportListState);

  const fetchTemplates = async () => {
    try {
      const response = templateResponse;
      if (type === "분석") {
        const filteredTemplates = response.templates.filter((item: any) => item.type === "분석");

        setTemplateList(filteredTemplates);
      } else {
        const filteredTemplates = response.templates.filter((item: any) => item.type === "분류");
        setTemplateList(filteredTemplates);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [type]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 4,
          px: 6,
          py: 4,
          overflow: "hidden",
          background: "linear-gradient(to right, #2563EB, #9333EA)",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 10 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#fff",
              mb: 1,
              fontSize: { xs: "1.875rem", md: "2.25rem" },
            }}
          >
            {t("CreateNewReport")}
          </Typography>

          <Typography
            sx={{
              color: "rgba(239, 246, 255, 1)",
              maxWidth: 600,
            }}
          >
            현재 생성된 리포트가 없습니다. 리포트를 생성하고 관리하세요.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#F9FAFB" }}>
        <Typography variant="h6">템플릿 선택</Typography>
        <Typography>리포트를 만들기 위한 다양한 템플릿을 선택하세요</Typography>
        <Box sx={{ display: "flex", borderRadius: 2, gap: 2, mt: 2, py: 1 }}>
          {templateList.map((template, idx) => (
            <Box key={idx} sx={{ width: 300 }}>
              <TemplateCard
                reportItem={template}
                index={idx}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
                setDateRange={setDateRange}
              />
            </Box>
          ))}
        </Box>
        <Divider sx={{ my: 2 }} />
        {/* TODO: 추후 사용자들이 직접 생성한 템플릿도 제공할지 고려 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
          }}
        >
          <Typography>또는 빈 리포트로 시작하기</Typography>
          <Button
            sx={{
              ...ContainedButtonStyles,
              backgroundColor: "#000",
              "&:hover": {
                backgroundColor: "#1F2937",
              },
            }}
            onClick={() => {
              setIsCreatingReport(true);
            }}
          >
            빈 리포트 만들기
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// 각 템플릿 항목 카드
const TemplateCard = ({
  reportItem,
  index,
  selectedPeriod,
  setSelectedPeriod,
  setDateRange,
}: {
  reportItem: templateItem;
  index: number;
  selectedPeriod: periodItems | null;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<periodItems | null>>;
  setDateRange: (dateRange: { start_date: string; end_date: string }) => void;
}) => {
  const imageUrl = [report1, report3, report4, report5];
  const [openModal, setOpenModal] = useState(false);
  const [templateContents, setTemplateContents] = useState<ContentItem[]>([]);
  const setSelectedReportList = useSetRecoilState(selectedReportListState);
  const setIsCreatingReport = useSetRecoilState(isCreatingReportState);

  const handleTemplateClick = () => {
    console.log(reportItem);
    setTemplateContents(JSON.parse(reportItem.contents));
    setOpenModal(true);
  };

  const handleCreate = async () => {
    try {
      const contentsWithIds = templateContents.map((item: any) => {
        const uniqueRandomNumber = String(Math.floor(100000 + Math.random() * 900000));
        return {
          ...item,
          id: `${item.type}-${uniqueRandomNumber}`,
        };
      });
      setSelectedReportList(contentsWithIds);
      const tempDateRange = getDateRangeFromPeriod(selectedPeriod!);
      if (tempDateRange) {
        setDateRange(tempDateRange);
      }
      setIsCreatingReport(true);
      // onCreate({ date_range: tempDateRange, contents: contentsWithIds });
    } catch (error) {
      console.error(error);
    }
    setOpenModal(false);
  };

  const getDateRangeFromPeriod = (selectedPeriod: {
    year?: number;
    quarter?: number;
  }): { start_date: string; end_date: string } => {
    if (!selectedPeriod?.year) {
      return { start_date: "", end_date: "" };
    }

    const { year, quarter } = selectedPeriod;

    if (quarter) {
      switch (quarter) {
        case 1:
          return { start_date: `${year}-01-01`, end_date: `${year}-03-31` };
        case 2:
          return { start_date: `${year}-04-01`, end_date: `${year}-06-30` };
        case 3:
          return { start_date: `${year}-07-01`, end_date: `${year}-09-30` };
        case 4:
          return { start_date: `${year}-10-01`, end_date: `${year}-12-31` };
        default:
          return { start_date: "", end_date: "" };
      }
    }

    // 연도만 선택된 경우
    return {
      start_date: `${year}-01-01`,
      end_date: `${year}-12-31`,
    };
  };

  const handlePeriodChange = (period: { year: number; quarter?: number }) => {
    setSelectedPeriod(period);
  };
  return (
    <>
      <Card
        id={`report-template-${index}`}
        onClick={handleTemplateClick}
        sx={{
          position: "relative",
          borderRadius: 2,
          boxShadow: 1,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-4px)",
          },
        }}
      >
        <Box sx={{ position: "relative", height: 128, overflow: "hidden" }}>
          <CardMedia
            component="img"
            image={imageUrl[index % imageUrl.length]}
            alt={reportItem.name}
            sx={{
              height: 128,
              width: "100%",
              objectFit: "cover",
              transition: "transform 0.5s ease",
              ".MuiCard-root:hover &": {
                transform: "scale(1.05)",
              },
            }}
          />
        </Box>
        <CardContent sx={{ padding: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>
              {reportItem.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="var(--text-secondary-color)">
            {reportItem.description || "-"}
          </Typography>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to bottom, rgba(37,99,235,0) 0%, rgba(98, 145, 247, 0.7) 100%)",
              opacity: 0,
              transition: "opacity 0.3s ease",
              "&:hover": {
                opacity: 1,
              },
              display: "flex",
              alignItems: "flex-end",
              p: 2,
            }}
          >
            <Typography variant="body1" color="common.white" fontWeight={500}>
              Click to create
            </Typography>
          </Box>
        </CardContent>
      </Card>
      {openModal && (
        <Modal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "60%",
              height: "80%",
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                position: "sticky",
                alignItems: "center",
                top: 0,
                zIndex: 999,
              }}
            >
              <Box>
                <Typography variant="h6">{reportItem.name}</Typography>
                {reportItem.description !== "" && <Typography variant="body1">{reportItem.description}</Typography>}
              </Box>
              <Box>
                <IconButton onClick={() => setOpenModal(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Divider />
            <Box>
              <PeriodSelector
                type={reportItem.name.includes("분기") ? "quarterly" : "yearly"}
                onChange={handlePeriodChange}
              />
            </Box>
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    my: 1,
                    fontWeight: "bold",
                  }}
                >
                  <BackupTableIcon sx={{ color: "var(--button-color)", mr: "5px" }} />
                  {t("LayoutPreview")}
                </Typography>
                {templateContents.length > 0 && (
                  <Typography variant="body2">
                    각 섹션 구성의 크기와 배치를 확인하세요. 해당 항목들은 모두 수정이 가능합니다.
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, mt: 2 }}>
                {templateContents.length > 0 ? (
                  // <GridPreview contents={templateContents} />
                  <GridPreview contents={JSON.parse(reportItem.contents)} />
                ) : (
                  <EmptyTemplate />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
                // position: "sticky",
                bottom: 0,
                zIndex: 999,
              }}
            >
              <Button id="create-report-btn" sx={{ ...ContainedButtonStyles }} onClick={handleCreate}>
                {t("Create")}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};
